import {
  Action,
  Actions,
  Attributes,
  AttributesRow,
  Card,
  Flag,
  FlagProps,
} from 'platform/components';
import {Box, Center, HStack, Heading, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {
  MailboxConnectionStatus,
  UserMailboxResponseBody,
  useGetUserMailboxConnectionStatusQuery,
} from '@dms/api/core';
import {getSmtpProviderLogo} from '@dms/features/email-smtp';
import i18n from '@dms/i18n';

import {buildArray, DOT_CHARACTER, parseDate} from 'shared/web';

import {getProviderLabel} from '../utils/getProviderLabel';

interface UserMailboxCardProps {
  mailbox: UserMailboxResponseBody;
  onDelete: (id: string) => void;
  onReauthenticate: (id: string) => void;
}

export function UserMailboxCard(props: UserMailboxCardProps) {
  const {mailbox} = props;
  const formatDateTime = useDateTimeFormatter();

  const {data: connectionStatus} = useGetUserMailboxConnectionStatusQuery({
    mailboxId: mailbox.id,
  });

  const status = connectionStatus?.status ?? 'CONNECTING';
  const statusFlag = getStatusFlag(status);
  const providerLabel = getProviderLabel(mailbox.type);
  const providerLogo = getSmtpProviderLogo(mailbox.type);

  const rows: AttributesRow[] = [
    {
      label: i18n.t('general.labels.createdAt'),
      value: formatDateTime('dateTimeShort', parseDate(mailbox.createdAt)),
    },
  ];

  const actions = buildArray<Action>()
    .when(needsReauth(status) && mailbox.type !== 'imap', {
      type: 'button',
      title: i18n.t('page.myProfile.receivingMailbox.reauthenticate'),
      variant: 'ghostLink',
      leftIcon: 'communication/vpn_key',
      onClick: () => props.onReauthenticate(mailbox.id),
    })
    .add({
      type: 'button',
      title: i18n.t('general.actions.delete'),
      variant: 'dangerGhost',
      leftIcon: 'action/delete',
      onClick: () => props.onDelete(mailbox.id),
    });

  return (
    <Card key={mailbox.id} variant="inlineGrey">
      <VStack spacing={4}>
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <Box height={12} width={12}>
              <Center height="100%" width="100%">
                {providerLogo}
              </Center>
            </Box>
            <Center>
              <Heading size={4}>{`${DOT_CHARACTER} ${mailbox.name}`}</Heading>
            </Center>
          </HStack>
          <Actions actions={actions} />
        </HStack>
        <HStack spacing={2}>
          <Flag label={providerLabel} colorScheme="orange" isSubtle />
          <Flag
            label={statusFlag.label}
            colorScheme={statusFlag.colorScheme}
            isSubtle={statusFlag.isSubtle}
          />
        </HStack>
        <Attributes size="third" rows={rows} />
      </VStack>
    </Card>
  );
}

function getStatusFlag(
  status: MailboxConnectionStatus
): Required<Pick<FlagProps, 'label' | 'colorScheme' | 'isSubtle'>> {
  return match(status)
    .with('CONNECTED', () => ({
      label: i18n.t('page.myProfile.receivingMailbox.status.connected'),
      colorScheme: 'green' as const,
      isSubtle: true,
    }))
    .with('CONNECTING', () => ({
      label: i18n.t('page.myProfile.receivingMailbox.status.connecting'),
      colorScheme: 'blue' as const,
      isSubtle: true,
    }))
    .with('ERROR', () => ({
      label: i18n.t('page.myProfile.receivingMailbox.status.error'),
      colorScheme: 'red' as const,
      isSubtle: true,
    }))
    .with('AUTH_ERROR', () => ({
      label: i18n.t('page.myProfile.receivingMailbox.status.authError'),
      colorScheme: 'orange' as const,
      isSubtle: true,
    }))
    .exhaustive();
}

function needsReauth(status: MailboxConnectionStatus): boolean {
  return status === 'AUTH_ERROR' || status === 'ERROR';
}
